import { useEffect, useState } from "react";
import { api } from "./api";
import { Badge, Empty, Icon, dateTime, errorMessage } from "./components";

import type { Preferences, Notification } from "./api";
export function PreferencesPanel() {
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    api
      .preferences()
      .then((value) => {
        if (active) setPreferences(value.preferences);
      })
      .catch((error) => {
        if (active) setError(errorMessage(error));
      });
    return () => {
      active = false;
    };
  }, []);
  async function save() {
    if (!preferences) return;
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      setPreferences((await api.savePreferences(preferences)).preferences);
      setSaved(true);
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel form-stack">
      <h2>Mes préférences</h2>
      <p className="muted">
        Choisissez les informations utiles que vous souhaitez retrouver dans
        Avalon.
      </p>
      {preferences ? (
        <>
          <label className="check-label">
            <input
              type="checkbox"
              checked={preferences.notifications.introductions}
              onChange={(e) => {
                setPreferences({
                  ...preferences,
                  notifications: { introductions: e.target.checked },
                });
                setSaved(false);
              }}
            />
            Notifications de mises en relation, activités et réservations dans Avalon
          </label>
          <p className="field-help">
            Les demandes et leurs réponses restent consultables dans la Guilde,
            même si vous désactivez ces notifications.
          </p>
          <label className="check-label">
            <input
              type="checkbox"
              checked={preferences.discovery_personalization}
              onChange={(e) => {
                setPreferences({
                  ...preferences,
                  discovery_personalization: e.target.checked,
                });
                setSaved(false);
              }}
            />
            Autoriser des découvertes selon mon profil
          </label>
          <p className="field-help">
            Ce choix est conservé pour les découvertes personnalisées à venir.
            La recherche actuelle d’Ava répond aux besoins que vous lui
            transmettez explicitement.
          </p>
          <button className="button primary" disabled={busy} onClick={save}>
            {busy ? "Enregistrement…" : "Enregistrer mes préférences"}
            <Icon name="check" />
          </button>
        </>
      ) : (
        !error && <p className="muted">Chargement des préférences…</p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {saved && (
        <p className="success" role="status">
          Vos préférences sont enregistrées.
        </p>
      )}
      <p className="note">
        Les notifications push et les e-mails automatiques ne sont pas encore
        raccordés. Ces préférences ne donnent aucun consentement de prospection
        commerciale.
      </p>
    </section>
  );
}
export function NotificationsPanel({ onRequests, onNotification }: { onRequests: () => void; onNotification?: (notification: Notification) => void }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    api
      .notifications()
      .then((value) => {
        if (active) setNotifications(value.notifications);
      })
      .catch((error) => {
        if (active) setError(errorMessage(error));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);
  async function markRead(id: string) {
    setBusy(id);
    setError("");
    try {
      await api.readNotification(id);
      setNotifications((await api.notifications()).notifications);
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setBusy(null);
    }
  }
  return (
    <>
      <div className="section-heading">
        <h2>Mes notifications</h2>
        <Badge>
          {notifications.filter((n) => !n.read_at).length} non lue(s)
        </Badge>
      </div>
      <p className="muted">
        Les changements utiles à vos mises en relation, activités et réservations.
      </p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {loading ? (
        <p className="muted">Chargement…</p>
      ) : !notifications.length ? (
        <Empty icon="check" title="Tout est calme ici.">
          Aucune notification à consulter pour le moment. Vos demandes restent
          accessibles dans la Guilde.
        </Empty>
      ) : (
        <div className="stack">
          {notifications.map((n) => (
            <article
              key={n.id}
              className={`panel ${n.read_at ? "" : "notification-unread"}`}
            >
              <div className="section-heading">
                <h3>
                  {n.kind.startsWith("event_") ? "Votre participation à une activité évolue" : n.kind.startsWith("space_reservation_") ? "Votre demande d’espace évolue" : n.kind === "introduction_requested" ||
                  n.kind === "introduction_request"
                    ? "Une demande vous attend"
                    : n.kind.includes("accept")
                      ? "Un accord a été donné"
                      : "Votre mise en relation évolue"}
                </h3>
                <Badge>{n.read_at ? "Lue" : "À lire"}</Badge>
              </div>
              <p className="muted">
                {dateTime(new Date(n.created_at * 1000).toISOString())}
              </p>
              <div className="button-row">
                <button className="text-button" onClick={() => onNotification ? onNotification(n) : onRequests()}>
                  {n.kind.startsWith("event_") ? "Voir mon inscription" : n.kind.startsWith("space_reservation_") ? "Voir ma réservation" : "Voir mes demandes"} <Icon name="arrow" />
                </button>
                {!n.read_at && (
                  <button
                    className="button"
                    disabled={busy === n.id}
                    onClick={() => void markRead(n.id)}
                  >
                    Marquer comme lue
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
      <p className="fineprint">
        C’est tout pour le moment. Les notifications distantes ne sont pas
        activées.
      </p>
    </>
  );
}
